// ── Types — Single source of truth for all API response shapes ──

export interface EmployeeOverview {
  id: number;
  fullName: string;
  jobTitle: string;
  totalWorkedSeconds: number;
  totalPausedSeconds: number;
  avgActivityPercent: number;
  isCheckedIn: boolean;
  isCurrentlyPaused: boolean;
  eventCount: number;
}

export interface PauseBlock {
  start: string;
  end: string | null;
  durationSeconds: number;
}

export interface Session {
  checkIn: string;
  checkOut: string | null;
  workedSeconds: number;
  pausedSeconds: number;
  pauses: PauseBlock[];
}

export interface ActivityEvent {
  id: number;
  action: "check_in" | "check_out" | "pause" | "resume";
  timestamp: string;
  ip: string | null;
  location: string | null;
}

export interface TrackedActivity {
  id: number;
  timestamp: string;
  app: string | null;
  windowTitle: string | null;
  input: "keyboard" | "mouse" | "both" | "none" | "system" | null;
  activityPercent: number | null;
  displayName: string;
  category: string;
}

export interface Screenshot {
  id: number;
  timestamp: string;
  screenshotUrl: string;
  displayId: string | null;
}

export interface ManualEntry {
  id: number;
  fromTime: string;
  toTime: string;
  title: string | null;
  description: string | null;
  clientName: string | null;
}

export interface TopApp {
  app: string;
  minutes: number;
  percent: number;
  category: string;
}

export interface HourlyActivity {
  hour: number;
  avgPercent: number;
  minutesTracked: number;
}

export interface CategoryBreakdown {
  category: string;
  minutes: number;
  percent: number;
  color: string;
  apps: string[];
}

export interface ActivitySummary {
  totalWorkedSeconds: number;
  totalPausedSeconds: number;
  totalActiveMinutes: number;
  totalIdleMinutes: number;
  avgActivityPercent: number;
  totalScreenshots: number;
  totalManualHours: number;
  topApps: TopApp[];
  activityByHour: HourlyActivity[];
  categoryBreakdown: CategoryBreakdown[];
}

export interface UserActivityData {
  employee: {
    id: number;
    fullName: string;
    jobTitle: string;
    photo: string | null;
    isCheckedIn: boolean;
    isCurrentlyPaused: boolean;
  };
  date: string;
  attendance: {
    id: number;
    totalWorkedSeconds: number;
    totalPausedSeconds: number;
    isLegacy: boolean;
  } | null;
  events: ActivityEvent[];
  sessions: Session[];
  activities: TrackedActivity[];
  screenshots: Screenshot[];
  manualEntries: ManualEntry[];
  summary: ActivitySummary;
}

export interface DashboardOverview {
  totalUsers: number;
  todayClockedIn: number;
  activeNowCount: number;
  avgActivityPercent: number;
  totalWorkedSeconds: number;
  totalScreenshotsToday: number;
  totalPauseSeconds: number;
}

export interface HeatmapDataPoint {
  date: string;
  hour: number;
  activityPercent: number;
}

export interface TopAppItem {
  app: string;
  totalMinutes: number;
  percentage: number;
  category: string;
}

export interface DashboardTopAppsResponse {
  topApps: TopAppItem[];
  categoryBreakdown: CategoryBreakdown[];
}

export interface UserStats {
  totalHours: number;
  avgDailyHours: number;
  avgActivityPercent: number;
  topApp: string | null;
  activeDays: number;
  totalDays: number;
}

export interface AttendanceHistoryEntry {
  date: string;
  employee: { id: number; fullName: string; jobTitle: string | null } | null;
  clockIn: string | null;
  clockOut: string | null;
  totalWorkedSeconds: number;
  totalPausedSeconds: number;
  status: "complete" | "still_in" | "no_data";
  isManualEntry?: boolean;
  manualEntryNote?: string | null;
  events: Array<{
    id: number;
    type: string;
    timestamp: string;
    note: string | null;
    ip: string | null;
    location: string | null;
  }>;
}

export interface AttendanceAuditLogEntry {
  id: number;
  employeeId: number;
  employeeName: string;
  changedBy: number;
  changedByName: string;
  date: string;
  changeType: string;
  oldCheckInTime: string | null;
  newCheckInTime: string | null;
  oldCheckOutTime: string | null;
  newCheckOutTime: string | null;
  reason: string | null;
  createdAt: string;
}

export interface DeviceInfo {
  id: number;
  userId: number;
  deviceId: string;
  hostname: string | null;
  platform: string | null;
  firstSeen: string;
  lastSeen: string;
}

export interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  jobTitle: string | null;
  accessLevel: string | null;
  avatarUrl: string | null;
  platformRole: "PLATFORM_ADMIN" | "CRM_MEMBER" | "SUBSCRIBER";
}

export interface Organization {
  id: string;
  name: string;
  logoUrl: string | null;
  timezone: string;
  role: string; // user's role within this org
}

export interface OrgMembership {
  id: string;
  orgId: string;
  orgName: string;
  role: string;
}

export interface Member {
  id: string;
  userId: number;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  role: string;
  jobTitle: string | null;
  isActive: boolean;
  joinedAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
}

export interface ApiKeyItem {
  id: string;
  name: string;
  hint: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PlanItem {
  id: string;
  name: string;
  slug: string;
  pricePerSeatCents: number;
  maxSeats: number;
  features: { list: string[]; flags?: Record<string, boolean> };
  trialDays: number;
}

export interface SubscriptionInfo {
  id: string;
  planId: string;
  status: string;
  seatCount: number;
  usedSeats?: number;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  plan?: PlanItem;
}

// Category color map — must match the backend CATEGORY_COLORS constant
export const CATEGORY_COLORS: Record<string, string> = {
  Productivity: "#22c55e",
  Communication: "#3b82f6",
  Browsing: "#f59e0b",
  Design: "#a855f7",
  Documents: "#06b6d4",
  Entertainment: "#ef4444",
  Other: "#6b7280",
};
